import React from "react";
import { Camera, Eye, Search } from "lucide-react";
import { motion } from "framer-motion";

const GalleryHero = ({
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  categories,
  totalItems,
}) => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#0F172A] via-[#111827] to-[#030712] text-white pt-24 pb-16 px-4 sm:px-6 lg:px-12 -mt-20">
      {/* Background Glows */}
      <div className="absolute top-0 right-1/4 w-[28rem] h-[28rem] bg-rose-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[32rem] h-[32rem] bg-red-700/15 rounded-full blur-3xl pointer-events-none" />

      {/* Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: "36px 36px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto text-center">
        {/* Top Badge */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs sm:text-sm font-semibold text-rose-300 tracking-wide uppercase mb-6"
        >
          <Camera className="w-4 h-4 text-rose-400" />
          <span>Curated Architectural Showcase</span>
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] max-w-5xl mx-auto"
        >
          Signature Projects &{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-rose-400 to-amber-300">
            Property Gallery
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 text-base sm:text-xl text-gray-300 max-w-3xl mx-auto font-normal leading-relaxed"
        >
          Explore high-definition photo tours, luxury French rowhouses, iconic high-rise towers, 
          ready-to-move rental homes, and completed turnkey interior transformations.
        </motion.p>

        {/* Search & Filter Bar */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-10 max-w-2xl mx-auto relative"
        >
          <div className="relative flex items-center bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl p-2 shadow-2xl focus-within:border-red-500/80 transition-all">
            <Search className="w-5 h-5 text-gray-400 ml-4 shrink-0" />
            <input
              type="text"
              placeholder="Search by project name, location (e.g. Whitefield, Thanisandra), or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent px-4 py-3 text-sm sm:text-base text-white placeholder-gray-400 focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white mr-2 bg-white/10 rounded-lg transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
          className="mt-8 flex flex-wrap justify-center gap-2 sm:gap-3"
        >
          {categories.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer ${
                  isActive
                    ? "bg-red-600 text-white shadow-lg shadow-red-600/40 scale-105"
                    : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </motion.div>

        {/* Live counter */}
        <div className="mt-8 text-xs sm:text-sm text-gray-400 flex items-center justify-center gap-2">
          <Eye className="w-4 h-4 text-red-400" />
          <span>Showing {totalItems} verified showcase spaces</span>
        </div>
      </div>
    </section>
  );
};

export default GalleryHero;
